import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Spin, Tag, Tooltip } from "antd";
import dayjs from "dayjs";
import { fetchDocExpPlacements } from "./doc-exp-api";
import { formatDate, formatMoney, pick, toFlag, toNumber } from "./doc-exp-utils";

const VID_REZERV = {
  CHECK_IN: 1,
  DAY: 0,
  CHECK_OUT: -1
};

const VID_REZERV_META = {
  [VID_REZERV.CHECK_IN]: { label: "Заезд", color: "green", short: "З" },
  [VID_REZERV.DAY]: { label: "День", color: "blue", short: "Д" },
  [VID_REZERV.CHECK_OUT]: { label: "Отъезд", color: "orange", short: "О" }
};

const WEEKDAYS = ["пн", "вт", "ср", "чт", "пт", "сб", "вс"];

const MEALS = [
  { key: "pit1", short: "З", title: "Завтрак" },
  { key: "pit2", short: "О", title: "Обед" },
  { key: "pit3", short: "П", title: "Полдник" },
  { key: "pit4", short: "У", title: "Ужин" }
];

const formatTime = (value) => {
  if (!value) return "";
  const d = dayjs(value);
  return d.isValid() ? d.format("HH:mm") : "";
};

const dayKey = (value) => {
  if (!value) return "";
  const d = dayjs(value);
  return d.isValid() ? d.format("YYYY-MM-DD") : "";
};

const dayTotal = (item) =>
  (Number(item.syma) || 0) + (Number(item.symaPit) || 0) + (Number(item.symaLech) || 0);

const mergeMeals = (items) => {
  const out = { pit1: false, pit2: false, pit3: false, pit4: false };
  (items || []).forEach((it) => {
    MEALS.forEach((m) => {
      if (it[m.key]) out[m.key] = true;
    });
  });
  return out;
};

const normalizePlacement = (row, index) => ({
  key: `${pick(row, "fdate", "Fdate") ?? ""}-${pick(row, "idNumber", "IdNumber") ?? index}-${index}`,
  fdate: pick(row, "fdate", "Fdate") ?? null,
  idNumber: toNumber(pick(row, "idNumber", "IdNumber")) ?? 0,
  numberName: pick(row, "numberName", "NumberName") ?? "",
  catName: pick(row, "catName", "CatName") ?? "",
  vidRezerv: toNumber(pick(row, "vidRezerv", "VidRezerv")) ?? 0,
  timeRezerv: pick(row, "timeRezerv", "TimeRezerv") ?? null,
  cnt: toNumber(pick(row, "cnt", "Cnt")) ?? 0,
  priceName: pick(row, "priceName", "PriceName") ?? "",
  priceStrName: pick(row, "priceStrName", "PriceStrName") ?? "",
  priceStrNamePit: pick(row, "priceStrNamePit", "PriceStrNamePit") ?? "",
  priceStrNameLech: pick(row, "priceStrNameLech", "PriceStrNameLech") ?? "",
  syma: toNumber(pick(row, "syma", "Syma")) ?? 0,
  symaPit: toNumber(pick(row, "symaPit", "SymaPit")) ?? 0,
  symaLech: toNumber(pick(row, "symaLech", "SymaLech")) ?? 0,
  discMain: toNumber(pick(row, "discMain", "DiscMain")) ?? 0,
  discPit: toNumber(pick(row, "discPit", "DiscPit")) ?? 0,
  discLech: toNumber(pick(row, "discLech", "DiscLech")) ?? 0,
  pit1: toFlag(pick(row, "pit1", "Pit1")),
  pit2: toFlag(pick(row, "pit2", "Pit2")),
  pit3: toFlag(pick(row, "pit3", "Pit3")),
  pit4: toFlag(pick(row, "pit4", "Pit4")),
  isHoliday: toFlag(pick(row, "isHoliday", "IsHoliday"))
});

const MealDots = ({ meals, compact }) => {
  const active = MEALS.filter((m) => meals[m.key]);
  const title = active.length
    ? active.map((m) => m.title).join(", ")
    : "Без питания";
  return (
    <Tooltip title={title}>
      <span
        className={`doc-exp-card__place-meals${compact ? " is-compact" : ""}`}
        aria-label={title}
      >
        {MEALS.map((m) => (
          <span
            key={m.key}
            className={`doc-exp-card__place-meal${meals[m.key] ? " is-on" : ""}`}
            data-meal={m.short}
          >
            {compact ? null : m.short}
          </span>
        ))}
      </span>
    </Tooltip>
  );
};

const CostLine = ({ label, amount, name, disc }) => {
  const n = Number(amount) || 0;
  if (!n && !name && !Number(disc)) return null;
  return (
    <div className="doc-exp-card__place-cost-line">
      <div className="doc-exp-card__place-cost-main">
        <span className="doc-exp-card__place-cost-label">{label}</span>
        <span className="doc-exp-card__place-cost-amount">{n ? formatMoney(n) : "—"}</span>
      </div>
      {name || disc ? (
        <div className="doc-exp-card__place-cost-meta">
          {name ? <span className="doc-exp-card__place-cost-name">{name}</span> : null}
          {disc ? <span className="doc-exp-card__place-cost-disc">−{formatMoney(disc)}</span> : null}
        </div>
      ) : null}
    </div>
  );
};

const DayDetail = ({ item }) => {
  const vid = VID_REZERV_META[item.vidRezerv] || VID_REZERV_META[VID_REZERV.DAY];
  const room = [item.numberName, item.catName].filter(Boolean).join(" · ");
  const time = formatTime(item.timeRezerv);
  const total = dayTotal(item);
  const meals = {
    pit1: item.pit1,
    pit2: item.pit2,
    pit3: item.pit3,
    pit4: item.pit4
  };
  const hasMeals = MEALS.some((m) => meals[m.key]);
  const costLines = [
    {
      label: "Размещение",
      amount: item.syma,
      name: item.priceStrName || item.priceName,
      disc: item.discMain
    },
    {
      label: "Питание",
      amount: item.symaPit,
      name: item.priceStrNamePit,
      disc: item.discPit
    },
    {
      label: "Лечение",
      amount: item.symaLech,
      name: item.priceStrNameLech,
      disc: item.discLech
    }
  ].filter((line) => Number(line.amount) > 0 || line.name || Number(line.disc) > 0);

  return (
    <div className="doc-exp-card__place-detail-item">
      <div className="doc-exp-card__place-detail-head">
        <Tag className="doc-exp-card__place-vid" color={vid.color}>
          {vid.label}
        </Tag>
        {item.isHoliday ? (
          <Tooltip title="Цена по тарифу выходного дня">
            <Tag className="doc-exp-card__place-holiday-tag" color="magenta">
              Вых.
            </Tag>
          </Tooltip>
        ) : null}
        {room ? <span className="doc-exp-card__place-day-room">{room}</span> : null}
        {time ? <span className="doc-exp-card__place-day-time">{time}</span> : null}
        {item.cnt > 1 ? <span className="doc-exp-card__place-day-cnt">×{item.cnt}</span> : null}
        <span className="doc-exp-card__place-day-total">{total ? formatMoney(total) : "—"}</span>
      </div>
      {hasMeals ? (
        <div className="doc-exp-card__place-detail-meals">
          <span className="doc-exp-card__place-detail-meals-label">Питание</span>
          <MealDots meals={meals} />
        </div>
      ) : null}
      {costLines.length ? (
        <div className="doc-exp-card__place-costs">
          {costLines.map((line) => (
            <CostLine key={line.label} {...line} />
          ))}
        </div>
      ) : null}
    </div>
  );
};

const formatCellSum = (value) => {
  const n = Number(value) || 0;
  if (!n) return "";
  return Math.round(n).toLocaleString("ru-RU");
};

/**
 * Вкладка «Размещения» — календарная сетка дней + детализация по клику.
 */
const DocExpPlacementsPanel = ({ idDoc, active, reloadToken = 0 }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedKey, setSelectedKey] = useState("");
  const reqRef = useRef(0);
  const loadedKeyRef = useRef("");

  useEffect(() => {
    if (!active) return undefined;
    if (idDoc == null) {
      setRows([]);
      setError("");
      setSelectedKey("");
      loadedKeyRef.current = "";
      return undefined;
    }

    const key = `${idDoc}:${reloadToken}`;
    if (loadedKeyRef.current === key) return undefined;

    const requestId = ++reqRef.current;
    setLoading(true);
    setError("");
    fetchDocExpPlacements(idDoc)
      .then((data) => {
        if (requestId !== reqRef.current) return;
        const next = Array.isArray(data) ? data.map(normalizePlacement) : [];
        setRows(next);
        loadedKeyRef.current = key;
        setSelectedKey((prev) => {
          if (prev && next.some((r) => dayKey(r.fdate) === prev)) return prev;
          return next[0] ? dayKey(next[0].fdate) : "";
        });
      })
      .catch((e) => {
        if (requestId !== reqRef.current) return;
        setRows([]);
        setSelectedKey("");
        loadedKeyRef.current = "";
        setError(e.message || "Не удалось загрузить размещение");
      })
      .finally(() => {
        if (requestId === reqRef.current) setLoading(false);
      });

    return undefined;
  }, [active, idDoc, reloadToken]);

  const byDate = useMemo(() => {
    const map = new Map();
    rows.forEach((item) => {
      const key = dayKey(item.fdate);
      if (!key) return;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(item);
    });
    return map;
  }, [rows]);

  const calendarCells = useMemo(() => {
    if (!rows.length) return [];
    const keys = [...byDate.keys()].sort();
    const start = dayjs(keys[0]).startOf("day");
    const end = dayjs(keys[keys.length - 1]).startOf("day");
    const pad = (start.day() + 6) % 7;
    const gridStart = start.subtract(pad, "day");
    const endPad = (7 - ((end.day() + 6) % 7) - 1 + 7) % 7;
    const gridEnd = end.add(endPad, "day");

    const cells = [];
    for (let d = gridStart; d.isBefore(gridEnd) || d.isSame(gridEnd, "day"); d = d.add(1, "day")) {
      const key = d.format("YYYY-MM-DD");
      const items = byDate.get(key) || null;
      const inRange = !d.isBefore(start, "day") && !d.isAfter(end, "day");
      const total = items ? items.reduce((s, it) => s + dayTotal(it), 0) : 0;
      const vid = items?.[0]?.vidRezerv;
      const isHoliday = items ? items.some((it) => it.isHoliday) : false;
      cells.push({
        key,
        date: d,
        items,
        inRange,
        total,
        vid,
        meals: items ? mergeMeals(items) : null,
        isHoliday,
        isWeekend: d.day() === 0 || d.day() === 6
      });
    }
    return cells;
  }, [rows, byDate]);

  const monthLabel = useMemo(() => {
    if (!rows.length) return "";
    const first = dayjs(rows[0].fdate);
    const last = dayjs(rows[rows.length - 1].fdate);
    if (!first.isValid()) return "";
    if (first.isSame(last, "month")) return first.format("MMMM YYYY");
    return `${first.format("MMM YYYY")} – ${last.format("MMM YYYY")}`;
  }, [rows]);

  const selectedItems = selectedKey ? byDate.get(selectedKey) || [] : [];

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc.syma += Number(r.syma) || 0;
        acc.symaPit += Number(r.symaPit) || 0;
        acc.symaLech += Number(r.symaLech) || 0;
        return acc;
      },
      { syma: 0, symaPit: 0, symaLech: 0 }
    );
  }, [rows]);

  const grand = totals.syma + totals.symaPit + totals.symaLech;

  if (idDoc == null) {
    return (
      <Alert type="info" showIcon message="Сохраните путёвку, чтобы увидеть размещение" />
    );
  }

  if (loading && rows.length === 0) {
    return (
      <div className="doc-exp-card__center">
        <Spin />
      </div>
    );
  }

  return (
    <div className="doc-exp-card__placements">
      {error ? (
        <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />
      ) : null}
      {loading ? <Spin size="small" className="doc-exp-card__placements-spin" /> : null}
      {!loading && !error && rows.length === 0 ? (
        <div className="doc-exp-card__placements-empty">Нет размещения</div>
      ) : null}

      {rows.length ? (
        <>
          <div className="doc-exp-card__place-cal">
            {monthLabel ? (
              <div className="doc-exp-card__place-cal-month">{monthLabel}</div>
            ) : null}
            <div className="doc-exp-card__place-cal-weekdays">
              {WEEKDAYS.map((wd) => (
                <div key={wd} className="doc-exp-card__place-cal-wd">
                  {wd}
                </div>
              ))}
            </div>
            <div className="doc-exp-card__place-cal-grid">
              {calendarCells.map((cell) => {
                if (!cell.inRange && !cell.items) {
                  return <div key={cell.key} className="doc-exp-card__place-cal-cell is-pad" />;
                }
                const activeDay = cell.key === selectedKey;
                const vidMeta = VID_REZERV_META[cell.vid] || null;
                const sumText = formatCellSum(cell.total);
                return (
                  <button
                    key={cell.key}
                    type="button"
                    className={[
                      "doc-exp-card__place-cal-cell",
                      cell.items ? "is-day" : "is-empty",
                      cell.isWeekend ? "is-weekend" : "",
                      cell.isHoliday ? "is-holiday" : "",
                      activeDay ? "is-selected" : "",
                      cell.vid === VID_REZERV.CHECK_IN ? "is-in" : "",
                      cell.vid === VID_REZERV.CHECK_OUT ? "is-out" : ""
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    disabled={!cell.items}
                    onClick={() => cell.items && setSelectedKey(cell.key)}
                    title={cell.isHoliday ? "Выходной тариф" : undefined}
                  >
                    <span className="doc-exp-card__place-cal-top">
                      <span className="doc-exp-card__place-cal-num">{cell.date.date()}</span>
                      <span className="doc-exp-card__place-cal-badges">
                        {cell.isHoliday ? (
                          <span className="doc-exp-card__place-cal-holiday" title="Выходной тариф">
                            В
                          </span>
                        ) : null}
                        {vidMeta && cell.vid !== VID_REZERV.DAY ? (
                          <span className="doc-exp-card__place-cal-vid" title={vidMeta.label}>
                            {vidMeta.short}
                          </span>
                        ) : null}
                      </span>
                    </span>
                    {cell.meals ? <MealDots meals={cell.meals} compact /> : (
                      <span className="doc-exp-card__place-meals is-compact is-spacer" aria-hidden />
                    )}
                    <span className="doc-exp-card__place-cal-sum">
                      {sumText || (cell.items ? "—" : "\u00a0")}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {selectedItems.length ? (
            <div className="doc-exp-card__place-detail">
              <div className="doc-exp-card__place-detail-title">
                <span>{formatDate(selectedItems[0].fdate)}</span>
                <span className="doc-exp-card__place-detail-wd">
                  {dayjs(selectedItems[0].fdate).format("dd")}
                </span>
              </div>
              {selectedItems.map((item) => (
                <DayDetail key={item.key} item={item} />
              ))}
            </div>
          ) : null}

          {grand > 0 ? (
            <footer className="doc-exp-card__place-footer">
              <span className="doc-exp-card__place-footer-label">Итого</span>
              <div className="doc-exp-card__place-footer-parts">
                {totals.syma > 0 ? <span>разм. {formatMoney(totals.syma)}</span> : null}
                {totals.symaPit > 0 ? <span>пит. {formatMoney(totals.symaPit)}</span> : null}
                {totals.symaLech > 0 ? <span>леч. {formatMoney(totals.symaLech)}</span> : null}
              </div>
              <span className="doc-exp-card__place-footer-total">{formatMoney(grand)}</span>
            </footer>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

export default DocExpPlacementsPanel;
